<script setup>
import { ref, watch, computed, useSlots } from 'vue';
import EnTextField from 'Components/inputs/EnTextField/EnTextField.vue';
import EnSkeleton from 'Components/base/EnSkeleton/EnSkeleton.vue';
import { useI18n } from 'vue-i18n';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import { PencilIcon } from 'lucide-vue-next';

const props = defineProps({
  /**
   * The array of fields to display and edit.
   */
  fields: Array,
  /**
   * Heading of the section
   */
  sectionHeading: String,
  /**
   * If defined, overrides the disabled state of the save button (when in edit mode).
   */
  saveDisabledOverride: {
    type: Boolean,
    default: undefined,
  },
  /**
   * Text for an additional action button in the top right corner.
   */
  actionButtonText: {
    type: String,
    default: null,
  },
  /**
   * If true, the action button is disabled.
   */
  actionButtonDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * If false, the edit button is not shown and editing is disabled.
   */
  hasData: {
    type: Boolean,
    default: true,
  },
  /**
   * If true, the fields can be edited.
   */
  editable: {
    type: Boolean,
    default: true,
  },
  /**
   * If true, the edit button click is handled externally via the 'edit-button-clicked' event.
   */
  customEdit: {
    type: Boolean,
    default: false,
  },
  /**
   * If defined, controls the editing state from parent component.
   */
  isEditing: {
    type: Boolean,
    default: undefined,
  },
  /**
   * If true, the component is in a loading state and shows skeletons instead of data.
   */
  isLoading: {
    type: Boolean,
    default: false,
  },
  /**
   * Number of skeleton rows to show when loading.
   * Default is 3.
   */
  skeletonRowCount: {
    type: Number,
    default: 3,
  },
});

const { t } = useI18n();
const slots = useSlots();
const emit = defineEmits(['update-fields', 'edit-button-clicked', 'update:isEditing', 'action-button-click', 'validation-failed', 'local-fields-changed']);

const isEditing = ref(false);
const originalFields = ref([]);
const localFields = ref([]);
const showMap = ref({});

const hasChanges = computed(() => {
  if (localFields.value.length !== originalFields.value.length) return true;

  return localFields.value.some((field, i) => {
    return field.data !== originalFields.value[i].data;
  });
});

function validateLocalFields() {
  const errors = [];
  localFields.value.forEach((field, index) => {
    if (field.rules && Array.isArray(field.rules)) {
      for (let rule of field.rules) {
        try {
          let isValid = rule(field.data, localFields.value);
          if (isValid === true) {
            continue;
          }
          // If the rule returns a string instead of true, it represents an error message.
          if (typeof isValid === 'string') {
            errors.push({index, key: field.key, message: isValid});
            break;
          }

          // Fallback: try the rule with just (value).
          // If it does not return true, treat it as an error.
          isValid = rule(field.data);
          if (isValid !== true) {
            errors.push({index, key: field.key, message: typeof isValid === 'string' ? isValid : 'Invalid'});
            break;
          }
        } catch (e) {
          errors.push({index, key: field.key, message: e.message || 'Invalid'});
          break;
        }
      }
    }
  });
  return errors;
}

const hasValidationErrors = computed(() => validateLocalFields().length > 0);

const saveIsDisabled = computed(() => {
  if (props.saveDisabledOverride !== undefined) return !!props.saveDisabledOverride;
  const changes = hasChanges.value;
  const validationErrors = hasValidationErrors.value;

  return (!changes || validationErrors);
});

const fieldsHaveData = computed(() => {
  return props.fields.some(fieldHasData);
});

function fieldHasData(field) {
  const value = field.data;
  return value && value.toString().trim();
}

const hasNoDataSlot = computed(() => {
  return slots['no-data-text'];
});

const showNoDataSlot = computed(() => {
  return (!fieldsHaveData.value || !props.hasData) && hasNoDataSlot.value;
});

watch(
    () => props.fields,
    (newFields) => {
      localFields.value = newFields.map(field => ({...field}));
      originalFields.value = newFields.map(field => ({...field}));
      const map = {};
      newFields.forEach((field, index) => {
        const id = field.key ?? index;
        map[id] = false;
      });
      showMap.value = map;
    },
    {immediate: true}
);

watch(
    () => localFields.value,
    (val) => {
      emit('local-fields-changed', val);
    },
    {deep: true}
);

watch(
    () => props.isEditing,
    (newEditing) => {
      if (newEditing !== undefined) isEditing.value = newEditing;
    },
    {immediate: true}
);

function handleEditButtonClick() {
  if (props.customEdit === true) {
    emit('edit-button-clicked');
  } else {
    isEditing.value = true;
    if (props.isEditing !== undefined) emit('update:isEditing', true);
  }
}

function cancelEdit() {
  localFields.value = originalFields.value.map(field => ({...field}));
  isEditing.value = false;
  if (props.isEditing !== undefined) emit('update:isEditing', false);
}

function saveEdit() {
  const errors = validateLocalFields();
  if (errors.length) {
    emit('validation-failed', errors);
    return;
  }

  originalFields.value = localFields.value.map(field => ({...field}));
  emit('update-fields', originalFields.value);
  isEditing.value = false;
  if (props.isEditing !== undefined) emit('update:isEditing', false);
}

function toggleShow(key) {
  const id = key ?? '';
  showMap.value[id] = !showMap.value[id];
}

function wrapRules(field) {
  if (!field || !field.rules) return undefined;
  return field.rules.map(rule => (val) => {
    const res = rule(val, localFields.value);
    if (res === true || typeof res === 'string') return res;
    return rule(val);
  });
}

</script>

<template>
  <div class="section-container">
    <div class="top-section">
      <div class="section-heading">
        <slot name="sectionHeading">{{ props.sectionHeading }}</slot>
      </div>
      <div class="right-actions" v-if="!isLoading">
        <slot name="actionButtons"></slot>

        <en-button v-if="props.actionButtonText" :static-id="props.actionButtonText + '-button'" class="action-button" :disabled="props.actionButtonDisabled" @click="$emit('action-button-click')" >
          {{ props.actionButtonText }}
        </en-button>

        <template v-if="editable">
          <en-button class="action-button" static-id="edit-button" variant="outline" size="xs" v-if="!isEditing" @click="handleEditButtonClick" >
            <pencil-icon></pencil-icon>
            {{ t('common.edit') }}
          </en-button>

          <div class="action-buttons" v-else>
            <en-button static-id="cancel-button" height="25px" class="action-button" @click="cancelEdit" >
              {{ t('common.cancel') }}
            </en-button>
            <en-button :class="{ '--disabled': saveIsDisabled, 'action-save-enabled': !saveIsDisabled }" static-id="save-button" class="action-button" height="25px" variant="default" :disabled="saveIsDisabled" @click="saveEdit" >{{ t('common.save') }}
            </en-button>
          </div>
        </template>
      </div>
    </div>

    <div class="banner-slot-wrapper" v-if="!isLoading && $slots.changeRequestBanner">
      <slot name="changeRequestBanner"></slot>
    </div>

    <div class="fields-view-container">
      <template v-if="isLoading && localFields.length === 0">
        <template v-for="i in skeletonRowCount" :key="i">
          <en-skeleton width="w-28" height="h-6"></en-skeleton>
          <en-skeleton width="w-48" height="h-6"></en-skeleton>
        </template>
      </template>

      <template v-else-if="showNoDataSlot">
        <div class="no-data">
          <slot name="no-data-text"></slot>
        </div>
      </template>

      <template v-else>
        <div v-for="(field, index) in localFields" :key="index" class="field-container">
          <div class="field-label relative">{{ field.fieldLabel }}<span v-if="field.changed" class="changed-dot absolute -right-3.5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[var(--en-color-danger)]"></span></div>

          <div v-if="isLoading" class="field-data">
            <en-skeleton width="w-48" height="h-6"></en-skeleton>
          </div>

          <template v-else-if="field.custom">
            <slot :name="field.slotName || field.fieldLabel"/>
          </template>

          <div class="editable-input-field" v-else-if="isEditing && field.editable !== false">
            <template v-if="field.type === 'password'">
              <en-text-field
                  v-model="field.data"
                  :rules="wrapRules(field) || field.rules"
                  :type="showMap[field.key ?? index] ? 'text' : 'password'"
                  :append-icon="showMap[field.key ?? index] ? 'mdi-eye' : 'mdi-eye-off'"
                  :id="`local-field-${field.slotName}-${index}-input`"
                  outlined
                  @click:append="toggleShow(field.key ?? index)"
              />
            </template>
            <template v-else>
              <en-text-field
                  v-model="field.data"
                  :id="`local-field-${field.slotName}-${index}-input`"
                  :rules="wrapRules(field) || field.rules"
                  :type="field.type || 'text'"
                  :outlined="field.outlined !== false"></en-text-field>
            </template>
          </div>

          <div :class="['field-data', field.key ? `field-key-${field.key}` : '']" v-else>
            <template v-if="Array.isArray(field.data) && field.data.length">
              <div v-for="(line, lineIdx) in field.data" :key="lineIdx">{{ line }}</div>
            </template>
            <template v-else>{{ field.data }}</template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables";

.section-container {

  .action-button {
    display: flex;
    padding: 0.3rem 0.5rem;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    line-height: 100%;

    border-radius: 0.4375rem;
    border: 1px solid variables.$pagination-slot-button-border-color;
    background: variables.$en-0;
    box-shadow: 0px 1px 2.8px 0px variables.$pagination-slot-button-box-shadow-color;
    color: variables.$pagination-slot-button-main-text-color;

    cursor: pointer;
    transition: all .2s;

    &:hover {
      box-shadow: 0px 2px 5px 0px variables.$pagination-slot-button-box-shadow-color-hover;
      border: 1px solid variables.$pagination-slot-button-border-color-hover;
    }
  }

  .top-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;

    .section-heading {
      color: variables.$en-blue;
      font-size: 1.1rem;
    }

    .action-buttons {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: .5rem;

      .--disabled {
        opacity: .5;
        pointer-events: none;
      }

      .action-save-enabled {
        background: variables.$en-blue;
        color: variables.$en-0;
        border-color: rgba(variables.$en-blue, 0.1);
      }

      .action-save-enabled:hover {
        filter: brightness(0.95);
      }
    }
  }

  .banner-slot-wrapper {
    width: 100%;
    margin: 0 0 0.75rem 0;
  }


  .fields-view-container {
    width: 100%;
    display: grid;
    row-gap: 1rem;
    column-gap: 2rem;
    grid-template-columns: max-content 1fr;
    padding: 1rem;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;

    border-radius: 8px;
    border: 1px solid rgba(13, 48, 72, 0.17);
    background: variables.$en-0;

    .field-container {
      display: contents;
      align-items: flex-start;
      width: 100%;

      .field-label {
        color: variables.$en-70;
        white-space: nowrap;
      }

      .field-data, .editable-input-field {
        width: 100%;
      }

      .field-data {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 1.5rem;
      }

      .field-data-empty {
        color: variables.$en-50;
      }

      .editable-input-field {

        :deep(.v-text-field, .v-input) {
          margin-top: 0 !important;
          padding: 3px 9px;
          font-size: 1rem !important;
          line-height: 1rem !important;
        }

      }
    }
  }
}

@media (max-width: 640px) {
  .fields-view-container {
    grid-template-columns: 1fr;
    column-gap: 0.5rem;
    padding: 0.75rem;
  }

  .field-label {
    white-space: normal;
    margin-bottom: 0.25rem;
  }

  .field-data, .editable-input-field {
    width: 100%;
  }
}

:deep(.v-field__input) {
  font-size: 1rem !important;
  line-height: 1rem !important;
  border-radius: 4px;
  border: 0.691px solid #DCDCDC;
  padding: 3px 9px !important;
  background: variables.$en-0;
  min-height: 0 !important;
}
</style>